import { defineDocument } from '../../schema';
export const gameMemeMashDocument = defineDocument({
    id: 'game:meme-mash',
    kind: 'game',
    slug: 'meme-mash',
    route: '/discover/games/meme-mash',
    seo: {
        title: 'Discover Meme Mash',
        description: "A party game where you fill meme templates and remix others' answers. Score points when your creative fills get used. 4 rounds of laughs.",
        imageAsset: 'coverMemeMash',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Meme Mash',
            },
        ],
        description: [
            {
                type: 'text',
                text: "Players fill in ad-lib style meme templates, remix each other's answers, and earn points when their answers are used.",
            },
        ],
        imageAsset: 'coverMemeMash',
        imageAlt: 'Meme Mash game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Each round starts with a blank meme template. Everyone fills it in with their best, most original answers. Then it's remix time: sift through the wild pile of responses to find the hidden gems and rebuild the meme.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'You earn points every time someone uses your answer in a remix. At the end, the player with the most points earns the title of Supreme Meme Lord—and all the pretend power that comes with it.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '3-12 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~18 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'September 23, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '67d1c7c8d303f4b1017260a4',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'One player selects a meme template.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'There are three blank spaces in the template to be filled. Players fill the blanks with their own creative answers.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "Players enter remix mode: sift through the pool of responses to rebuild the meme template using other players' answers.",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The best meme is derived from the most popular answers chosen for each blank space during the remix phase.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The meme image is generated based on the best meme.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Players are awarded points for the round.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat for four rounds with different meme templates.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: '100 points every time one of your answers is used in a remixed meme. If multiple players submitted the same answer, points earned from the remix are split evenly among the submitting players.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The player(s) with the highest score at the end of the game wins.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Answers for blanks in meme templates',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Meme template options',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Meme images',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: medium',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: streamers, creative groups, meme enthusiasts, remote games',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/airhead-games-officially-launches',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Meme Mash blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '67d1c7c8d303f4b1017260a4',
                },
            ],
        },
    ],
});
//# sourceMappingURL=meme-mash.js.map